import {
  NotificationType,
  INotificationBaseRelatedToShow,
  IPayloadUpdateStandProductPurchases,
} from "@evennt-tech/api-v2-core";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import { getOptimizedSelector } from "store/utils/get-optimized-selector.utils";

type PurchasesByStand = Map<string, Record<string, number>>;

type State = {
  isLoading: boolean;
  items: PurchasesByStand;
};
type FetchManyParams = {
  showId: string;
  standId: string | string[];
};
type Action = {
  fetchMany: (params: FetchManyParams) => Promise<void>;
  autoCatch: (showId: string) => () => void;
  reset: () => void;
};
type Store = State & Action;

const productPurchasePerStandStore = create(
  devtools<Store>(
    (set, get) => ({
      isLoading: false,
      items: new Map(),
      fetchMany: async ({ showId, standId }) => {
        const { isLoading, items } = get();

        const standIds = (typeof standId === "string"
          ? [standId]
          : standId
        ).filter((id) => !items.has(id));
        if (isLoading || !standIds.length) {
          return;
        }

        set({ isLoading: true });
        try {
          const purchases = await apiV2.product.purchasePerStand.getMany({
            showId,
            standIds,
          });

          const newItems: PurchasesByStand = new Map(get().items);
          standIds.forEach((id) => newItems.set(id, newItems.get(id) ?? {}));
          purchases.forEach((purchase) => {
            const standPurchases = newItems.get(purchase.standId) ?? {};
            standPurchases[purchase.productId] = purchase.purchaseCount;
            newItems.set(purchase.standId, standPurchases);
          });

          set({
            items: newItems,
          });
        } catch (e) {
          console.error("Error occurred fetching product purchases: ", e);
        } finally {
          set({ isLoading: false });
        }
      },
      autoCatch: (showId) => {
        apiV2.notification.subscribe<
          INotificationBaseRelatedToShow<IPayloadUpdateStandProductPurchases>
        >({
          key: NotificationType.UPDATED_STAND_PRODUCT_PURCHASES,
          showId,
          callback: async ({ payload }) => {
            if (!payload) return;
            const { items } = get();
            const { standId } = payload;

            const shouldUpdate = items.has(standId);
            if (!shouldUpdate) {
              return;
            }

            set({ isLoading: true });
            try {
              const purchases = await apiV2.product.purchasePerStand.getMany({
                showId,
                standIds: [payload.standId],
              });
              const newItems = new Map(items);
              newItems.set(
                standId,
                purchases.reduce<Record<string, number>>((acc, curr) => {
                  acc[curr.productId] = curr.purchaseCount;
                  return acc;
                }, {})
              );

              set({ items: newItems });
            } catch (e) {
              console.error("Error occurred updating product purchases: ", e);
            } finally {
              set({ isLoading: false });
            }
          },
        });

        return () =>
          apiV2.notification.unsubscribe({
            key: NotificationType.UPDATED_STAND_PRODUCT_PURCHASES,
            showId,
          });
      },
      reset: () => set({ isLoading: false, items: new Map() }),
    }),
    { name: "product-purchase-per-stand" }
  )
);

export const useProductPurchasesPerStand = getOptimizedSelector(
  productPurchasePerStandStore
);

export const useProductPurchasesPerStandActions = () =>
  useProductPurchasesPerStand(
    ({ autoCatch, fetchMany, reset }) =>
      ({
        autoCatchProductPurchasesPerStand: autoCatch,
        fetchManyProductPurchasesPerStand: fetchMany,
        resetProductPurchasesPerStand: reset,
      } as const)
  );

export const useProductPurchaseCount = (productId: string, standId?: string) =>
  useProductPurchasesPerStand(({ items }) => {
    if (!standId) return 0;
    return items.get(standId)?.[productId] ?? 0;
  });
